import { useEffect, useRef, useState } from "react";
import { t } from "i18next";
import { ICON_BASE_PATH } from "@/app/utils/config";
import ApprovalProgress, { getStatusLabelFromCode } from "./ApprovalProgress";
import {
  gxBeneficiaryDetailsApi,
  gxBeneficiaryTransactionsSummaryApi,
  gxBeneficiaryTransactionDetailsApi,
} from "@/app/apiConfig/services/paymentsAbstractApiLayer";
import type {
  BeneDetails,
  BeneficiaryData,
  BeneficiaryDataProps,
  Transaction,
  BeneficiaryTransactionDetailsPayload,
  BeneMetromapDto,
} from "@/app/types/beneficiaries";
import type { GenericPayload } from "@/app/types/genericTypes";
import ShimmerLoading from "@/app/components/webloading/shimmer/ShimmerLoading";
import { gxDateTimeFormat } from "@/app/utils/helpers/gxDateTimeFormat";
import { AppDrawerSingleFooter } from "@/app/models/drawer/AppDrawerSingleFooter";
import TransectionDetails from "./TransectionDetails";
import TransactionDetailsSkeleton from "../DetailsSkeleton/TransactionDetailsSkeleton";
import { gxCurrencyFormat } from "@/app/utils/helpers/gxCurrencyFormat";
import MaskedAccountNumber from "@/app/utils/helpers/MaskedAccountNumber";
import StatusBadge from "@/app/components/common/badge/StatusBadge";
import NoDataFound from "@/app/components/common/warning/NoDataFound";
import "../styles/Beneficiaries.scss"

const BeneficiaryView: React.FC<BeneficiaryDataProps> = ({
  rowdata,
  showTransactions = true,
  onActiveTabChange,
}) => {
  const [activeTab, setActiveTab] = useState<"beneficiary" | "transaction">(
    "beneficiary",
  );
  const [isLoading, setIsLoading] = useState(false);
  const [personalInfoOpen, setPersonalInfoOpen] = useState(true);
  const [accountDetailsOpen, setAccountDetailsOpen] = useState(true);
  const [approvalProgressDetailsOpen, setApprovalProgressDetailsOpen] =
    useState(true);
  const [data, setData] = useState<BeneDetails | null>(null);
  const [transactionData, setTransactionData] = useState<Transaction[]>([]);

  const [isTransactionDetailsDrawerOpen, setIsTransactionDetailsDrawerOpen] =
    useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState<any | null>(
    null,
  );
  const [loadingDetails, setLoadingDetails] = useState(false);

  const hasFetchedDetails = useRef(false);
  const hasFetchedTransactions = useRef(false);

  const getBeneficiaryViewApi = async (rowdata: BeneficiaryData) => {
    setIsLoading(true);
    try {
      const response = await gxBeneficiaryDetailsApi({
        beneId: rowdata.beneId,
        searchText: "",
        status: null,
        pageNo: 1,
        sortBy: "ASC",
        pageSize: 50,
      });

      if (response?.data) {
        const bene: BeneDetails = {
          beneName: response.data.beneName || "-",
          beneNickname: response.data.beneNickname || "-",
          emailId: response.data.emailId || "-",
          mobileCode: response.data.mobileCode || "-",
          mobileNo: response.data.mobileNo || "-",
          beneAddressLine1: response.data.beneAddressLine1 || "-",
          beneCountryName: response.data.beneCountryName || null,
          beneAccountNo: response.data.beneAccountNo || "-",
          maskBeneAccountNo: response.data.maskBeneAccountNo || "-",
          beneBankSwiftBicCode: response.data.beneBankSwiftBicCode || "-",
          beneCurrency: response.data.beneCurrency || "-",
          beneBankName: response.data.beneBankName || "-",
          beneBankAddressLine1: response.data.beneBankAddressLine1 || "-",
          crtdDt: response.data.crtdDt,
          crtdBy: response.data.crtdBy,
          updtDt: response.data.updtDt || "",
          updtBy: response.data.updtBy,
          crtdName: response.data.crtdName,
          recordStatusCodeDesc: response.data.recordStatusCodeDesc || "",
          authzHists: response.data.authzHists || [],
          recordStatusCode: Number(response.data.recordStatusCode) || 0,
          beneBankCountryCode2: response.data.beneBankCountryCode2 || "-",
          beneMetromapDto: response.data.beneMetromapDto || "",
          approvalDate: response.data.approvalDate,
          beneBankCountryName: response.data.beneBankCountryName,
        };

        setData(bene);
      }
    } catch (error) {
    } finally {
      setIsLoading(false);
    }
  };

  const gettransactionApi = async (rowdata: BeneficiaryData) => {
    setIsLoading(true);
    try {
      const requestPayload: GenericPayload = {
        id: rowdata?.beneId,
        pageNo: 1,
        sortBy: "ASC",
        pageSize: 50,
      };

      const response =
        await gxBeneficiaryTransactionsSummaryApi(requestPayload);

      if (response?.data) {
        setTransactionData(response?.data);
      }
    } catch (error) {
    } finally {
      setIsLoading(false);
    }
  };

  const fetchTransactionDetails = async (transId: number, gxChnNo: string) => {
    setLoadingDetails(true);
    try {
      const payload: BeneficiaryTransactionDetailsPayload = {
        transId: transId,
        gxChnNo: gxChnNo,
      };

      const response = await gxBeneficiaryTransactionDetailsApi(payload);

      if (response?.data) {
        setSelectedTransaction(response.data);
      }
    } catch (error) {
    } finally {
      setLoadingDetails(false);
    }
  };

  const getInitials = (fullName?: string): string => {
    if (!fullName || fullName === "--") return "--";

    const parts = fullName.trim().split(" ");
    if (parts.length === 1) {
      return parts[0].charAt(0).toUpperCase();
    }

    const firstInitial = parts[0].charAt(0).toUpperCase();
    const lastInitial = parts.at(-1)?.charAt(0).toUpperCase() ?? "";

    return firstInitial + lastInitial;
  };

  const handleTransactionClick = async (transaction: Transaction) => {
    setIsTransactionDetailsDrawerOpen(true);
    await fetchTransactionDetails(transaction.transId, transaction.gxChnNo);
  };

  const handleCloseTransactionDetails = () => {
    setIsTransactionDetailsDrawerOpen(false);
    setSelectedTransaction(null);
  };

  // useEffect(() => {
  //   if (!rowdata?.beneId) return;
  //   if (hasFetchedDetails.current) return;
  //   hasFetchedDetails.current = true;
  //   getBeneficiaryViewApi(rowdata);
  // }, [rowdata?.beneId]);

  // useEffect(() => {
  //   if (!rowdata?.beneId) return;
  //   if (!showTransactions) return;
  //   if (activeTab !== "transaction") return;
  //   if (hasFetchedTransactions.current) return;
  //   hasFetchedTransactions.current = true;
  //   gettransactionApi(rowdata);
  // }, [rowdata?.beneId, activeTab, showTransactions]);

  const fetchedDetailsForId = useRef<string | number | null>(null);
  const fetchedTxnsForId = useRef<string | number | null>(null);

  useEffect(() => {
    if (!rowdata?.beneId) return;
    if (fetchedDetailsForId.current === rowdata.beneId) return;
    fetchedDetailsForId.current = rowdata.beneId;
    getBeneficiaryViewApi(rowdata);
  }, [rowdata?.beneId]);

  useEffect(() => {
    if (!rowdata?.beneId) return;
    if (!showTransactions) return;
    if (activeTab !== "transaction") return;
    if (fetchedTxnsForId.current === rowdata.beneId) return;
    fetchedTxnsForId.current = rowdata.beneId;
    gettransactionApi(rowdata);
  }, [rowdata?.beneId, activeTab, showTransactions]);
  

  const handleTabChange = (tab: "beneficiary" | "transaction") => {
    setActiveTab(tab);
    onActiveTabChange?.(tab);
  };

  const parsedMetromapDto: BeneMetromapDto | null = (() => {
    if (!data?.beneMetromapDto) return null;
    if (typeof data.beneMetromapDto === "string") {
      try {
        return JSON.parse(data.beneMetromapDto);
      } catch {
        return null;
      }
    }
    return data.beneMetromapDto;
  })();

  // === Status label derived from recordStatusCode via centralized map ============
  const statusLabel = data
    ? getStatusLabelFromCode(data.recordStatusCode?.toString())
    : "";

  const renderTransactionDetails = () => {
    if (loadingDetails) {
      return (
        <div className="flex justify-center items-center p-8">
          <TransactionDetailsSkeleton />
        </div>
      );
    }

    if (selectedTransaction) {
      return <TransectionDetails transaction={selectedTransaction} />;
    }

    return <NoDataFound className="gx-no-data" message={"No data found"} />;
  };

  return (
    <div className="gx-beneficiary-view">
      {isLoading ? (
        <ShimmerLoading />
      ) : (
        <>
          {data ? (
            <div className="grid grid-cols-12 gap-2">
              <div className="col-span-12 gx-row-align-center">
                <div className="gx-beneficiary-title">
                  <h2 className="gx-h5-b">
                    {data ? getInitials(data?.beneName) : "--"}
                  </h2>
                </div>
              </div>

              <div className="col-span-12 gx-row-align-center">
                <div className="gx-beneficiary-username">
                  <h3 className="gx-h2-b">{data?.beneName || "--"}</h3>
                  <span className="gx-cell-date-text gx-p16-b">
                    {data?.beneNickname || "-"}
                  </span>
                  <span className="gx-bene-account-no">
                    <MaskedAccountNumber
                      accountNumber={data?.beneAccountNo ?? ""}
                      maskedAccountNumber={data?.maskBeneAccountNo ?? ""}
                      showToggle
                      toggleIconClassName="w-3 h-3 cursor-pointer opacity-70 hover:opacity-100"
                      showCopy
                    />
                  </span>

                  <span className="gx-cell-date-text gx-p14-b">
                    {data?.crtdDt ? gxDateTimeFormat(data?.crtdDt) : "--"}
                  </span>
                </div>
              </div>

              {showTransactions && (
                <div className="col-span-12 gx-row-align-center mb-5">
                  <div className="gx-tabs">
                    <button
                      className={`gx-tab-button gx-p12-b${
                        activeTab === "beneficiary"
                          ? "gx-tab-active"
                          : "gx-tab-inactive"
                      }`}
                      onClick={() => handleTabChange("beneficiary")}
                    >
                      {t("beneficiary_details")}
                    </button>
                    <button
                      className={`gx-tab-button gx-p12-b${
                        activeTab === "transaction"
                          ? "gx-tab-active"
                          : "gx-tab-inactive"
                      }`}
                      onClick={() => handleTabChange("transaction")}
                    >
                      {t("transaction_history")}
                    </button>
                  </div>
                </div>
              )}

              {(!showTransactions || (activeTab === "beneficiary" && data)) && (
                <>
                  <div className="col-span-12 gx-row-align-center gx-card">
                    <div
                      className={`gx-beneficiary-details ${
                        personalInfoOpen ? "expanded" : ""
                      }`}
                    >
                      <div className="gx-beneficiary-details-child">
                        <button
                          type="button"
                          onClick={() => setPersonalInfoOpen(!personalInfoOpen)}
                        >
                          <div className="gx-beneficiary-details-title">
                            <h2 className="gx-h3-b">
                              {t("bene_Personal_Info")}
                            </h2>
                          </div>
                          <div className="gx-beneficiary-details-img">
                            <div
                              className={`information-downarrow ${
                                personalInfoOpen ? "rotate" : ""
                              }`}
                            >
                              <img
                                src={`${ICON_BASE_PATH}down_arrow.svg`}
                                alt="Expand"
                              />
                            </div>
                          </div>
                        </button>
                      </div>

                      {personalInfoOpen && (
                        <div className="gx-beneficiary-details-list">
                          <table>
                            <tbody>
                              <tr>
                                <th className="gx-p12-m gx-label">
                                  {t("beneficiary_name")}
                                </th>
                                <th className="gx-p12-b">
                                  {data?.beneName || "--"}
                                </th>
                              </tr>
                              <tr>
                                <th className="gx-p12-m gx-label">
                                  {t("nick_name")}
                                </th>
                                <th className="gx-p12-b">
                                  {data?.beneNickname || "--"}
                                </th>
                              </tr>
                              <tr>
                                <th className="gx-p12-m gx-label">
                                  {t("email_id")}
                                </th>
                                <th className="gx-p12-b">
                                  {data?.emailId || "--"}
                                </th>
                              </tr>
                              <tr>
                                <th className="gx-p12-m gx-label">
                                  {t("mobile_no")}
                                </th>
                                <th className="gx-p12-b">{`${
                                  data.mobileCode || ""
                                } ${data?.mobileNo || ""}`}</th>
                              </tr>
                              <tr>
                                <th className="gx-p12-m gx-label gx-bene-address">
                                  {t("beneficiary_address")}
                                </th>
                                <th className="gx-p12-b gx-text-ellipsis">
                                  {data?.beneAddressLine1 || "--"}
                                </th>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="col-span-12 gx-row-align-center gx-card">
                    <div
                      className={`gx-beneficiary-details ${
                        accountDetailsOpen ? "expanded" : ""
                      }`}
                    >
                      <div className="gx-beneficiary-details-child">
                        <button
                          type="button"
                          onClick={() =>
                            setAccountDetailsOpen(!accountDetailsOpen)
                          }
                        >
                          <div className="gx-beneficiary-details-title">
                            <h2 className="gx-h3-b">
                              {t("bene_Account_Details")}
                            </h2>
                          </div>
                          <div className="gx-beneficiary-details-img">
                            <div
                              className={`information-downarrow ${
                                accountDetailsOpen ? "rotate" : ""
                              }`}
                            >
                              <img
                                src={`${ICON_BASE_PATH}down_arrow.svg`}
                                alt="Expand"
                              />
                            </div>
                          </div>
                        </button>
                      </div>

                      {accountDetailsOpen && (
                        <div className="gx-beneficiary-details-list">
                          <table>
                            <tbody>
                              <tr>
                                <th className="gx-p12-m gx-label">
                                  {t("account_number_iban")}
                                </th>
                                <th className="gx-p12-b">
                                  <MaskedAccountNumber
                                    accountNumber={data?.beneAccountNo ?? ""}
                                    maskedAccountNumber={
                                      data?.maskBeneAccountNo ?? ""
                                    }
                                    showToggle
                                    toggleIconClassName="w-3 h-3 cursor-pointer opacity-70 hover:opacity-100"
                                    showCopy
                                  />
                                </th>
                              </tr>
                              <tr>
                                <th className="gx-p12-m gx-label">
                                  {t("swift_Code_ifsc")}
                                </th>
                                <th className="gx-p12-b">
                                  {data?.beneBankSwiftBicCode || "--"}
                                </th>
                              </tr>

                              {/* ================== TEMP DELETE ====================== */}
                              {/* <tr> 
                                  <th className="gx-p12-m gx-label">
                                    {t("currency")}
                                  </th>
                                  <th className="gx-p12-b">
                                    {data?.beneCurrency
                                      ? gxCurrencyFormat(
                                          0,
                                          data.beneCurrency,
                                          undefined,
                                          {
                                            showSymbol: true,
                                            showCode: false,
                                          },
                                        ).symbol
                                      : "--"}
                                  </th>
                                </tr> */}

                              <tr>
                                <th className="gx-p12-m gx-label">
                                  {t("beneficiary_bank_name")}
                                </th>
                                <th className="gx-p12-b">
                                  {data?.beneBankName || "--"}
                                </th>
                              </tr>
                              <tr>
                                <th className="gx-p12-m gx-label">
                                  {t("beneficiary_bank_address")}
                                </th>
                                <th className="gx-p12-b">
                                  {data?.beneBankAddressLine1 || "--"}
                                </th>
                              </tr>
                              <tr>
                                <th className="gx-p12-m gx-label">
                                  {t("beneficiary_bank_country")}
                                </th>
                                <th className="gx-p12-b">
                                  {data?.beneBankCountryName || "--"}
                                </th>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="col-span-12 gx-row-align-center gx-card">
                    <div
                      className={`gx-beneficiary-details ${
                        approvalProgressDetailsOpen ? "expanded" : ""
                      }`}
                    >
                      <div className="gx-beneficiary-details-child">
                        <button
                          type="button"
                          onClick={() =>
                            setApprovalProgressDetailsOpen(
                              !approvalProgressDetailsOpen,
                            )
                          }
                        >
                          <div className="gx-beneficiary-details-title">
                            <h2 className="gx-h3-b">
                              {t("approval_progress")}
                            </h2>
                          </div>
                          <div className="gx-beneficiary-details-img">
                            <div
                              className={`information-downarrow ${
                                approvalProgressDetailsOpen ? "rotate" : ""
                              }`}
                            >
                              <img
                                src={`${ICON_BASE_PATH}down_arrow.svg`}
                                alt="Expand"
                              />
                            </div>
                          </div>
                        </button>
                      </div>
                      {approvalProgressDetailsOpen && (
                        <div className="gx-beneficiary-details-list">
                          <ApprovalProgress
                            data={{
                              recordStatusCode:
                                data.recordStatusCode?.toString(),
                              recordStatusCodeDesc: statusLabel,
                              crtdDt: data.crtdDt,
                              crtdName: data.crtdName,
                              updtDt: data.updtDt,
                              authzHists: data.authzHists || [],
                              beneMetromapDto: parsedMetromapDto,
                              noOfApprLevel:
                                parsedMetromapDto?.approvalDetails
                                  ?.approvalsRequired ||
                                data.authzHists?.length ||
                                0,
                              approvalsRequired:
                                parsedMetromapDto?.approvalDetails
                                  ?.approvalsRequired,
                              approvalsCompleted:
                                parsedMetromapDto?.approvalDetails
                                  ?.approvalsCompleted,
                              approvalDate: data.approvalDate,
                            }}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}

              {showTransactions &&
                activeTab === "transaction" &&
                (transactionData.length > 0 ? (
                  <div className="grid grid-cols-12 gap-4 transaction-row">
                    <div className="col-span-12">
                      <div className="gx-transaction-table">
                        <table>
                          <tbody>
                            {transactionData.map((txn) => (
                              <tr
                                className="gx-transaction-card"
                                key={txn.transId}
                                onClick={() => handleTransactionClick(txn)}
                                style={{ cursor: "pointer" }}
                              >
                                <th className="gx-p12-m">
                                  <div className="demo-user-child">
                                    <div className="demo-text gx-p12-b">
                                      {txn.debitAccountName
                                        ?.charAt(0)
                                        .toUpperCase()}
                                    </div>
                                    <div className="demo-user-title">
                                      <p className="gx-p14-sb">
                                        {txn?.debitAccountName}{" "}
                                        <span className="gx-p10 gx-th-date">
                                          {gxDateTimeFormat(
                                            txn?.crtdDt || "--",
                                          )}
                                        </span>
                                      </p>
                                    </div>
                                  </div>
                                </th>
                                <th className="gx-p12-m">
                                  <div className="demo-user-status gx-p10-sb">
                                    <p>
                                      <p className="gx-debit-currency gx-p12-sb">
                                        <span className="gx-currency-symbol">
                                          {
                                            gxCurrencyFormat(
                                              txn.debitAmount,
                                              txn.debitCurrency,
                                              undefined,
                                              {
                                                showSymbol: true,
                                                showCode: false,
                                              },
                                            ).symbol
                                          }
                                        </span>
                                        <span className="gx-currency-amount">
                                          {
                                            gxCurrencyFormat(
                                              txn.debitAmount,
                                              txn.debitCurrency,
                                              undefined,
                                              {
                                                showSymbol: true,
                                                showCode: false,
                                              },
                                            ).integer
                                          }
                                          <span className="gx-oo-gray">
                                            .00
                                          </span>
                                        </span>
                                      </p>

                                      <StatusBadge
                                        module="payments"
                                        statusCode={txn.paymentStatusCode}
                                      />
                                    </p>
                                  </div>
                                </th>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="gx-no-datafound">
                    <NoDataFound
                      className="gx-no-data"
                      message={"No Transactions Available"}
                    />
                  </div>
                ))}
            </div>
          ) : (
            <div className="gx-no-datafound">
              <NoDataFound
                className="gx-no-data"
                message={"No Transactions Available"}
              />
            </div>
          )}
        </>
      )}

      <AppDrawerSingleFooter
        title={t("transaction_details")}
        subtitle=""
        onClose={handleCloseTransactionDetails}
        submit=""
        onSubmit={() => {}}
        openDrawer={isTransactionDetailsDrawerOpen}
      >
        {renderTransactionDetails()}
      </AppDrawerSingleFooter>
    </div>
  );
};

export default BeneficiaryView;
